/**
 * Quali strumenti ha usato un agente, con quali argomenti e com'è andata.
 *
 * Richiesta di Enrico (16-08-2026): *«tenere traccia di tutto ed anche i log
 * degli strumenti utilizzati dagli agenti. Questo ci aiuterà nella fase di
 * monitoraggio e affinamento»*.
 *
 * Due letture perché rispondono a due domande diverse:
 *  - il **riepilogo** dice dove intervenire — quali strumenti l'agente usa
 *    davvero, quanto ci mettono, quanto sbagliano;
 *  - la **cronologia** serve quando una risposta specifica è andata storta:
 *    lì si guardano gli argomenti veri della chiamata.
 */
import { AlertTriangle, Wrench } from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAgenteStrumenti } from "@/hooks/useAdminAgenti";
import { cn } from "@/lib/utils";

const fmtMs = (v: number | null) => (v === null ? "—" : `${v} ms`);

function quando(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AgenteStrumenti({ slug }: { slug: string }) {
  const [soloErrori, setSoloErrori] = useState(false);
  const [tool, setTool] = useState<string | null>(null);
  const { data, isLoading } = useAdminAgenteStrumenti(slug, { tool, soloErrori });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const riepilogo = data?.riepilogo ?? [];
  const usi = data?.usi ?? [];

  if (!riepilogo.length && !usi.length) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <Wrench className="mx-auto mb-2 size-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Nessuno strumento usato finora da questo agente.
        </p>
        <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
          Il registro si popola da solo a ogni conversazione: dice quale strumento è stato
          chiamato, con quali argomenti e quanto ci ha messo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card">
        <header className="border-b px-4 py-2.5">
          <h3 className="text-sm font-semibold">Strumenti usati</h3>
          <p className="text-xs text-muted-foreground">
            Quali usa davvero, quanto ci mette, quanto sbaglia. Clicca per filtrare la
            cronologia.
          </p>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-muted/40 text-[11px] text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Strumento</th>
                <th className="px-4 py-2 text-right font-medium">Usi</th>
                <th className="px-4 py-2 text-right font-medium">Errori</th>
                <th className="px-4 py-2 text-right font-medium">Durata media</th>
                <th className="px-4 py-2 text-right font-medium">Peggiore</th>
                <th className="px-4 py-2 text-left font-medium">Ultimo uso</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {riepilogo.map((r) => (
                <tr
                  key={r.tool}
                  onClick={() => setTool(tool === r.tool ? null : r.tool)}
                  className={cn(
                    "cursor-pointer hover:bg-accent/40",
                    tool === r.tool && "bg-brand-50/60 dark:bg-brand-950/40"
                  )}
                >
                  <td className="px-4 py-2 font-mono text-xs">{r.tool}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.usi}</td>
                  <td
                    className={cn(
                      "px-4 py-2 text-right tabular-nums",
                      r.errori > 0 && "font-semibold text-red-700 dark:text-red-400"
                    )}
                  >
                    {r.errori}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {fmtMs(r.durata_media_ms)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {fmtMs(r.durata_max_ms)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {r.ultimo_uso ? quando(r.ultimo_uso) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border bg-card">
        <header className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
          <div>
            <h3 className="text-sm font-semibold">
              Cronologia
              {tool && <span className="ml-1.5 font-mono text-xs text-brand-700">{tool}</span>}
            </h3>
            <p className="text-xs text-muted-foreground">
              Gli argomenti veri di ogni chiamata: è qui che si capisce perché una risposta è
              uscita storta.
            </p>
          </div>
          <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={soloErrori}
              onChange={(e) => setSoloErrori(e.target.checked)}
              className="rounded"
            />
            Solo errori
          </label>
          {tool && (
            <button
              type="button"
              onClick={() => setTool(null)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Tutti gli strumenti
            </button>
          )}
        </header>
        {!usi.length ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Nessuna chiamata con questi filtri.
          </p>
        ) : (
          <ul className="divide-y">
            {usi.map((u) => (
              <li key={u.id} className="px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono font-semibold">{u.tool}</span>
                  {u.esito === "errore" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                      <AlertTriangle className="size-3" />
                      errore
                    </span>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground">
                      ok
                    </span>
                  )}
                  <span className="text-muted-foreground">{fmtMs(u.durata_ms)}</span>
                  {u.risposta_bytes !== null && (
                    <span className="text-muted-foreground/70">
                      risposta {u.risposta_bytes} B
                    </span>
                  )}
                  {u.richiesto_da_agente && (
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                      chiesto da {u.richiesto_da_agente}
                    </span>
                  )}
                  <span className="ml-auto text-muted-foreground">{quando(u.quando)}</span>
                </div>
                {u.argomenti && Object.keys(u.argomenti).length > 0 && (
                  <pre className="mt-1 overflow-x-auto rounded bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground">
                    {JSON.stringify(u.argomenti)}
                  </pre>
                )}
                {u.errore && (
                  <p className="mt-1 text-[11.5px] text-red-700 dark:text-red-400">{u.errore}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
